import { useSelector } from 'react-redux';
import {
  selectUser,
  selectUserStores,
  selectUserOutlets,
  selectUserPermissions,
} from '@/store/slices/authSlice';
import {
  selectCurrentStore as selectStoreSliceCurrentStore,
  selectCurrentOutlet as selectStoreSliceCurrentOutlet,
} from '@/store/slices/storeSlice';

/**
 * Hook for checking user access permissions, store/outlet access, and roles
 *
 * Uses storeSlice as the source of truth for currentStore/currentOutlet
 * to support explicit context pattern (frontend always sends storeId/outletId in requests)
 */
export const useHasAccess = () => {
  const user = useSelector(selectUser);
  const stores = useSelector(selectUserStores);
  const outlets = useSelector(selectUserOutlets);
  const permissions = useSelector(selectUserPermissions);

  // Use storeSlice as source of truth for current store/outlet selection
  const storeSelection = useSelector(selectStoreSliceCurrentStore);
  const outletSelection = useSelector(selectStoreSliceCurrentOutlet);

  // Build currentStore/currentOutlet objects from storeSlice
  const currentStore = storeSelection.storeId
    ? { id: storeSelection.storeId, name: storeSelection.storeName || '' }
    : null;
  const currentOutlet = outletSelection.outletId
    ? { id: outletSelection.outletId, name: outletSelection.outletName || '' }
    : null;

  // Get outlets for current store only
  const currentStoreOutlets = currentStore
    ? outlets.filter((outlet) => outlet.storeId === currentStore.id)
    : [];

  /**
   * Check if user has access to a specific store
   */
  const hasStoreAccess = (storeId: string): boolean => {
    return stores.some((store) => store.id === storeId && store.isActive);
  };

  /**
   * Check if user has access to a specific outlet
   */
  const hasOutletAccess = (outletId: string): boolean => {
    return outlets.some((outlet) => outlet.id === outletId && outlet.isActive);
  };

  /**
   * Check if user has a specific permission
   */
  const hasPermission = (permission: string): boolean => {
    return permissions.includes(permission);
  };

  /**
   * Check if user has any of the specified permissions
   */
  const hasAnyPermission = (permissionList: string[]): boolean => {
    return permissionList.some((permission) => permissions.includes(permission));
  };

  /**
   * Check if user has all of the specified permissions
   */
  const hasAllPermissions = (permissionList: string[]): boolean => {
    return permissionList.every((permission) => permissions.includes(permission));
  };

  /**
   * Check if user has minimum role in a store
   * Role hierarchy: STAFF < MANAGER < OWNER
   */
  const hasRole = (
    storeId: string,
    minRole: 'STAFF' | 'MANAGER' | 'OWNER'
  ): boolean => {
    const roleHierarchy = {
      STAFF: 1,
      MANAGER: 2,
      OWNER: 3,
    };

    const userStore = stores.find((store) => store.id === storeId);
    if (!userStore || !userStore.isActive) {
      return false;
    }

    const userRoleLevel = roleHierarchy[userStore.role as keyof typeof roleHierarchy];
    const minRoleLevel = roleHierarchy[minRole];

    return userRoleLevel >= minRoleLevel;
  };

  /**
   * Check if user has minimum role in current store
   */
  const hasCurrentStoreRole = (minRole: 'STAFF' | 'MANAGER' | 'OWNER'): boolean => {
    if (!currentStore) {
      return false;
    }
    return hasRole(currentStore.id, minRole);
  };

  /**
   * Check if user can access a route based on required permissions
   */
  const canAccessRoute = (requiredPermissions: string[] = []): boolean => {
    if (requiredPermissions.length === 0) {
      return true; // No permissions required
    }
    return hasAllPermissions(requiredPermissions);
  };

  /**
   * Check if user is in an outlet context
   */
  const hasOutletContext = (): boolean => {
    return currentOutlet !== null;
  };

  /**
   * Check if user is in a store context
   */
  const hasStoreContext = (): boolean => {
    return currentStore !== null;
  };

  return {
    user,
    currentStore,
    currentOutlet,
    stores,
    outlets,
    currentStoreOutlets,
    permissions,
    hasStoreAccess,
    hasOutletAccess,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasCurrentStoreRole,
    canAccessRoute,
    hasOutletContext,
    hasStoreContext,
  };
};
